import { AnimatePresence, motion, useAnimation } from 'framer-motion'
import React, { useEffect, useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { Button } from '@demandlocal/components'
import { ReactComponent as UploadIcon } from '../../images/icons/upload_creative.svg'
import { isEmpty, isUndefined } from 'lodash'
import { ReactComponent as ChevronDown } from '../../images/icons/icon_sortdown.svg'
import { ReactComponent as ChevronUp } from '../../images/icons/icon_sortup.svg'
import { ReactComponent as CheckMarkIcon } from '../../images/icons/green_check.svg'
import HoverMedia from '../HoverMedia'
import { DndProvider, DropTargetMonitor, useDrop } from 'react-dnd'
import { HTML5Backend, NativeTypes } from 'react-dnd-html5-backend'
import toast from 'react-hot-toast'

const ProductRadioInputCreativeUpload = ({
  product,
  name,
  creatives,
  setCreatives,
  type = 'image',
  disabled,
  value,
}: {
  name: string
  product: {
    product_code: string
    product_name: string
    product_description: string
    icon: any
  }
  creatives: { src: string; file: any }[]
  setCreatives: (prevCreatives: any) => void
  type?: 'image' | 'video' | 'audio'
  disabled?: boolean
  value: string | number
}): JSX.Element => {
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false)

  if (creatives.length > 3)
    throw new Error("The 'creatives' array length should not exceed 3.")

  return (
    <Controller
      name={'product'}
      render={({ field }) => (
        <div className={'flex'}>
          <input
            {...field}
            type="radio"
            id={`${name}-${value}`}
            name={name}
            value={product.product_code}
            className="hidden peer"
          />
          <label
            htmlFor={`${name}-${value}`}
            className={
              'transition ease-in-out duration-500 cursor-pointer border-2 flex flex-1 p-5 flex-col justify-center rounded-lg gap-5 peer-checked:border-primary-persian-blue peer-checked:bg-primary-persian-blue/10'
            }>
            <div className={'flex flex-row items-center'}>
              <span className={'self-start'}>{product.icon} </span>
              <div className={'flex flex-col gap-2 px-3'}>
                <span className={'text-sm font-bold'}>{product.product_name}</span>
                <span className={'text-xs'}>{product.product_description}</span>
                <AnimatePresence>
                  {field.value === value && (
                    <>
                      {isExpanded ? (
                        <motion.button
                          initial={{ opacity: 0, y: 0 }}
                          animate={{
                            opacity: 1,
                            y: 0,
                            transition: {
                              type: 'spring',
                              damping: 20,
                              stiffness: 150,
                              duration: 2,
                            },
                          }}
                          exit={{
                            opacity: 0,
                            y: 0,
                            transition: { duration: 0.5 },
                          }}
                          className={'flex flex-row items-center gap-2 w-fit'}
                          onClick={(e) => {
                            e.preventDefault()
                            setIsExpanded(false)
                          }}>
                          <span
                            className={'text-xs font-bold text-primary-persian-blue '}>
                            Use default creatives
                          </span>
                          <span>
                            <ChevronUp className={'stroke-primary-persian-blue'} />
                          </span>
                        </motion.button>
                      ) : (
                        <motion.button
                          className={'flex flex-row items-center gap-2 w-fit'}
                          initial={{ opacity: 0, y: 0 }}
                          animate={{
                            opacity: 1,
                            y: 0,

                            transition: {
                              type: 'spring',
                              damping: 20,
                              stiffness: 150,
                              duration: 2,
                            },
                          }}
                          exit={{
                            opacity: 0,
                            y: 0,
                            transition: { duration: 0.5 },
                          }}
                          onClick={(e) => {
                            e.preventDefault()
                            setIsExpanded(true)
                          }}>
                          <span
                            className={'text-xs font-bold text-primary-persian-blue '}>
                            Upload custom creatives
                          </span>
                          <span>
                            <ChevronDown className={'stroke-primary-persian-blue'} />
                          </span>
                        </motion.button>
                      )}
                    </>
                  )}
                </AnimatePresence>
                <AnimatePresence>
                  {field.value === value && (
                    <>
                      {isExpanded && (
                        <motion.div
                          className="flex flex-col gap-2"
                          initial={{ opacity: 0, y: -50 }}
                          animate={{
                            opacity: 1,
                            y: 0,
                            transition: {
                              type: 'spring',
                              damping: 20,
                              stiffness: 150,
                            },
                          }}
                          exit={{
                            opacity: 0,
                            y: 0,
                            transition: { duration: 0.5 },
                          }}>
                          <div className={'grid grid-cols-3 gap-5 py-5'}>
                            {creatives.map((creative: { src: string; file: any }, i) => {
                              return (
                                <div key={`${creative.src}-${i}`}>
                                  {!isEmpty(creative?.src) ? (
                                    <div
                                      className={
                                        'relative border-2 border-secondary-malachite-green bg-secondary-malachite-green/10 justify-center rounded-xl flex flex-col gap-3 justify-between col-span-1 h-full items-center p-5'
                                      }>
                                      <div className="absolute top-0 right-0 flex items-center justify-end">
                                        <div className="transform transform-translate translate-x-1/2 translate-y-1/2">
                                          <CheckMarkIcon />
                                        </div>
                                      </div>
                                      {type === 'image' ? (
                                        <img
                                          className={
                                            'object-scale-down w-fit self-center'
                                          }
                                          src={creative.src}
                                        />
                                      ) : type === 'video' ? (
                                        <HoverMedia
                                          src={creative.src}
                                          type={'video'}
                                          className={`rounded-xl w-fit h-fit object-cover object-scale-down max-h-40`}
                                        />
                                      ) : (
                                        <HoverMedia src={creative.src} type={'audio'} />
                                      )}
                                      <Button
                                        variant={'secondary'}
                                        animate={false}
                                        type={'button'}
                                        onClick={(e: any) => {
                                          e.preventDefault()
                                          setCreatives((prevCreatives: any) => {
                                            return prevCreatives.map(
                                              (
                                                creative: { src: string; file: any },
                                                index: number,
                                              ) => {
                                                if (index === i) {
                                                  return {
                                                    ...creative,
                                                    src: '',
                                                    file: undefined,
                                                  }
                                                }
                                                return creative
                                              },
                                            )
                                          })
                                        }}>
                                        Remove
                                      </Button>
                                    </div>
                                  ) : (
                                    <DndProvider backend={HTML5Backend}>
                                      <DragAndDrop
                                        product={product.product_code}
                                        onChange={(file: any) => {
                                          setCreatives((prevCreatives: any) => {
                                            return prevCreatives.map(
                                              (
                                                creative: { src: string; file: any },
                                                index: number,
                                              ) => {
                                                if (index === i) {
                                                  return {
                                                    ...creative,
                                                    src: URL.createObjectURL(file),
                                                    file: file,
                                                  }
                                                }
                                                return creative
                                              },
                                            )
                                          })
                                        }}
                                      />
                                    </DndProvider>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                          {product.product_code === 'mob' && (
                            <div className={'flex flex-col gap-1'}>
                              <p className={'text-sm font-bold'}>
                                Allowed file types: JPG/JPEG, PNG, GIF. Files should not
                                exceed 1MB
                              </p>
                            </div>
                          )}
                          {product.product_code === 'ott' && (
                            <div className={'flex flex-col gap-1'}>
                              <p className={'text-sm font-bold'}>
                                File quality 1080p (1920x1080). 1.6 - 3.2 MBPS recommended
                              </p>
                            </div>
                          )}
                          {product.product_code === 'aud' && (
                            <div className={'flex flex-col gap-1'}>
                              <p className={'text-sm font-bold'}>
                                Allowed file types: MP3, WMA, AAC, OGG, WAV. File should
                                not exceed 100MB
                              </p>
                              <p className={'text-sm font-bold'}>
                                Length: 30 sec or 15 sec
                              </p>
                            </div>
                          )}
                          {product.product_code === 'vid' && (
                            <div className={'flex flex-col gap-1'}>
                              <p className={'text-sm font-bold'}>
                                Video dimensions: minimum height of 144px and up to
                                4096x2160
                              </p>
                              <p className={'text-sm font-bold'}>
                                Aspect ratios: 16:9, 9:16, 4:3
                              </p>
                              <p className={'text-sm font-bold'}>
                                Video length: 15s or 30s
                              </p>
                              <p className={'text-sm font-bold'}>
                                All file types accepted except:{' '}
                                <em>
                                  Apple intermediate, HDV 720p60, Go2Meeting3, Avid
                                  Meridien, Windows Media Audio Lossless, Cineform, SWF
                                  (Flash)
                                </em>
                              </p>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </label>
        </div>
      )}
    />
  )
}

const DragAndDrop = ({
  onChange,
  product = 'image',
}: {
  product?: string
  onChange: any
}) => {
  const handleFileSelect = (file: any) => {
    onChange(file)
  }
  const acceptedFileTypes = useMemo(() => {
    switch (product) {
      case 'mob':
        return ['image/png', 'image/jpeg', 'image/jpg']
      case 'aud':
        return ['audio/mpeg', 'audio/x-ms-wma', 'audio/aac', 'audio/ogg', 'audio/wav']
    }
  }, [product])

  const maxSize = useMemo(() => {
    switch (product) {
      case 'mob':
        return 1 * 1024 * 1024
      case 'aud':
        return 100 * 1024 * 1024
      default:
        return 100 * 1024 * 1024
    }
  }, [product])

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item: { files: any[] }) {
        if (
          !isUndefined(acceptedFileTypes) &&
          !acceptedFileTypes.includes(item.files[0].type)
        ) {
          toast.error('Please see below for accepted file types')
          return
        }
        if (item.files[0].size > maxSize) {
          toast.error(`File size must not exceed ${maxSize / (1024 * 1024)}MB`)
          return
        }
        handleFileSelect(item.files[0])
      },
      canDrop(item: any) {
        return true
      },
      hover(item: any) {},
      collect: (monitor: DropTargetMonitor) => {
        const item = monitor.getItem() as any

        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        }
      },
    }),
    [],
  )

  return (
    <div
      className={`w-full font-Spartan text-primary-xiketic flex flex-col items-center gap-5 justify-center p-6 ${
        isOver ? 'bg-green-100' : 'bg-white'
      } border-dashed border-2 border-gray-200 rounded-lg`}
      ref={drop}>
      <div className={'flex flex-col items-center gap-3'}>
        <UploadIcon />
        <div className={'text-sm text-center'}>Drag and drop creative here</div>
        <div>
          <Button type={'button'} animate={false}>
            <label htmlFor={'upload_file'} className={'cursor-pointer border-1'}>
              Upload
            </label>
            <input
              type={'file'}
              id={'upload_file'}
              className={'hidden'}
              onChange={(e) => {
                e.preventDefault()
                if (e.target.files) {
                  const file = e.target.files[0]
                  if (file.name.includes(' ')) {
                    toast.error('Remove spaces from file name')
                    return
                  }
                  if (
                    !isUndefined(acceptedFileTypes) &&
                    !acceptedFileTypes.includes(file.type)
                  ) {
                    toast.error('Please see below for accepted file types')
                    return
                  }
                  if (file.size > maxSize) {
                    toast.error(`File must not exceed ${maxSize / (1024 * 1024)}MB`)
                    return
                  }
                  handleFileSelect(e?.target?.files[0])
                }
              }}
            />
          </Button>
        </div>
      </div>
      {product === 'mob' && (
        <div className={'flex flex-col items-center text-gray-300'}>
          <span className={'text-xs font-bold'}>Recommended sizes</span>
          <span className={'font-bold text-sm'}>300x250, 320x50, 300x50</span>
        </div>
      )}
    </div>
  )
}

export default ProductRadioInputCreativeUpload
